import { useAuth0 } from '@auth0/auth0-react';
import { ChatWithRequestPane } from '@strata/react-chat';
import { SuccessToast } from '@strata/react-toast-message';
import { saveAs } from 'file-saver';
import { type ComponentProps } from 'react';
import { useParams } from 'react-router';

import { getRequestAttachment } from 'api/payerRequestAPI';
import { AgoraTreatmentBaseUrl } from 'services/constants';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { usePostAttachFileMutation } from 'store/api/payerRequestApi';

interface ChatDetailsProps {
    canWriteRequestType: boolean;
    isLoading: boolean;
    conversationType: ComponentProps<typeof ChatWithRequestPane>['conversationType'];
    onMessageSend: ComponentProps<typeof ChatWithRequestPane>['onMessageSend'];
    onMessageDelete?: ComponentProps<typeof ChatWithRequestPane>['onMessageDelete'];
    requestDetails?: ComponentProps<typeof ChatWithRequestPane>['request'];
    notesForRequest?: ComponentProps<typeof ChatWithRequestPane>['notesForRequest'];
}

export const ChatDetails = ({
    canWriteRequestType,
    isLoading,
    conversationType,
    onMessageSend,
    onMessageDelete = undefined,
    requestDetails = undefined,
    notesForRequest = undefined,
}: ChatDetailsProps) => {
    const { user } = useAuth0();
    const { payerId, requestId } = useParams() as { payerId: string; requestId: string };

    const [postAttachFile] = usePostAttachFileMutation();

    return (
        <ChatWithRequestPane
            requestId={requestId}
            request={requestDetails}
            notesForRequest={notesForRequest}
            treatmentUrlBase={AgoraTreatmentBaseUrl(payerId)}
            requestUrlBase={AppRoutes.requestDetails(payerId, '')}
            conversationType={conversationType}
            disabled={!canWriteRequestType}
            isLoading={isLoading}
            userUPN={user?.email}
            isSupportUser
            onMessageSend={onMessageSend}
            onMessageDelete={onMessageDelete}
            onAddFile={async ({ file }) => {
                await postAttachFile({ payerId, requestId, attachment: file }).unwrap();
                SuccessToast({ message: 'File added successfully.' });
            }}
            onFileDownloadClick={async ({ requestAttachmentId, filename }) => {
                try {
                    const response = await getRequestAttachment({
                        payerId,
                        requestId,
                        requestAttachmentId,
                    });
                    saveAs(response, filename);
                } catch (err) {
                    handleError('An error occurred downloading attachment.', err as Error);
                }
            }}
        />
    );
};
